import { isLandingTrigger } from '@core/assistant/providers/providerUtils';
import { regionNameToCamelCase } from '@core/assistant/providers/Regions';
import { formatSpotRegion } from '@shared/utils/assistantScript';

import type { IAssistantScript } from '@shared/interfaces/assistantScript';
import type { IAssistantRagVectorStorage } from '@shared/interfaces/assistantRagStorage';

import { parseScriptSteps } from './scriptData';
import type {
  AiEntity,
  AiEntityCategory,
  AiEntityFilter,
  AiEntityKind,
  ContextState,
  OverallState,
  ScriptState,
} from './types';

// Region name prefixes we treat as spot regions when no map metadata is
// available. Matches the conventions used by UE blueprints + the existing
// formatSpotRegion helper. Lowercase comparison.
const SPOT_PREFIXES = ['spotregion', 'bpspotregion', 'ex_spot'];

export const normalizeTriggers = (
  scriptTrigger: string | string[] | undefined | null,
): string[] => {
  if (!scriptTrigger) {
    return [];
  }
  if (Array.isArray(scriptTrigger)) {
    return scriptTrigger.filter((t): t is string => Boolean(t));
  }
  return [scriptTrigger];
};

// Primary = first SpotRegion_* trigger when present, otherwise the first one.
// The rest become aliases. Keeps the mapping deterministic so the
// inspector header (`+N alias`) is stable across renders.
export const splitPrimaryAndAliases = (
  triggers: string[],
): { primary: string; aliases: string[] } => {
  if (triggers.length === 0) {
    return { primary: '', aliases: [] };
  }
  const spot = triggers.find((t) =>
    t.toLowerCase().startsWith('spotregion'),
  );
  const primary = spot ?? triggers[0];
  const aliases = triggers.filter((t) => t !== primary);
  return { primary, aliases };
};

export const getScriptKind = (script: IAssistantScript): AiEntityKind => {
  if (isLandingTrigger(script.scriptTrigger)) {
    return 'landing';
  }
  if (script.triggerType === 'object') {
    return 'object';
  }
  if (script.triggerType === 'ui_click') {
    return 'ui';
  }
  return 'area';
};

const isSpotTrigger = (trigger: string): boolean => {
  const lower = trigger.toLowerCase();
  return SPOT_PREFIXES.some((prefix) => lower.startsWith(prefix));
};

const isAiTriggerLike = (
  trigger: string,
  knownAiTriggerNames: Set<string>,
): boolean => {
  if (!trigger) {
    return false;
  }
  if (trigger.startsWith('AITrigger_')) {
    return true;
  }
  if (knownAiTriggerNames.has(trigger)) {
    return true;
  }
  // UE sends ad-hoc AI trigger names like "Expo #1". Best-effort heuristic:
  // ui_click scripts whose trigger isn't a known spot/region pattern fall
  // into the AI Triggers section.
  return false;
};

// Decide which section the entity belongs in. Categorization is
// triggerType-FIRST (MV-889): the runtime trigger semantics decide the bucket,
// not the trigger name. The two primary kinds the editor surfaces are:
//   - AI trigger (`area_entry`): auto-plays when the player walks into a zone.
//   - Spot (`ui_click`): shows a button in Noa's panel the player taps.
// `landing` (plays on join) and `object` (3D POI) keep their own categories so
// status logic stays precise; the UI folds them into the trigger/spot modes.
//
// The trigger-name heuristic (AITrigger_*, SpotRegion_*, knownAiTriggerNames)
// is ONLY a fallback for legacy scripts saved without a triggerType — it must
// never override an explicit triggerType, otherwise a script named
// "AITrigger_0" but saved as `ui_click` would land in the wrong section.
export const deriveCategory = (
  triggers: string[],
  triggerType: IAssistantScript['triggerType'] | undefined,
  knownAiTriggerNames: Set<string>,
): AiEntityCategory => {
  if (triggers.some((t) => t.toLowerCase() === 'landing')) {
    return 'landing';
  }
  // triggerType wins when present.
  if (triggerType === 'object') {
    return 'object';
  }
  if (triggerType === 'ui_click') {
    return 'spot';
  }
  if (triggerType === 'area_entry' || triggerType === 'custom_spot') {
    return 'ai_trigger';
  }
  // No triggerType (legacy scripts) — fall back to the name heuristic.
  if (triggers.some((t) => isAiTriggerLike(t, knownAiTriggerNames))) {
    return 'ai_trigger';
  }
  if (triggers.some((t) => isSpotTrigger(t))) {
    return 'spot';
  }
  return 'ai_trigger';
};

export const deriveScriptState = (
  script: IAssistantScript | null,
): ScriptState => {
  if (!script) {
    return 'none';
  }
  const steps = parseScriptSteps(script.scriptData);
  if (steps.length === 0) {
    return 'draft';
  }
  const hasNonEmpty = steps.some(
    (step) => typeof step.text === 'string' && step.text.trim().length > 0,
  );
  if (!hasNonEmpty) {
    return 'draft';
  }
  return 'ok';
};

export const deriveContextState = (
  items: IAssistantRagVectorStorage[],
): ContextState => {
  if (items.length === 0) {
    return 'none';
  }
  if (items.some((item) => item.status === 'INDEXING_FAILED')) {
    return 'failed';
  }
  if (items.every((item) => item.status === 'INDEXING_PENDING')) {
    return 'pending';
  }
  return 'ready';
};

export const deriveOverallState = (
  scriptState: ScriptState,
  contextState: ContextState,
): OverallState => {
  if (contextState === 'failed') {
    return 'error';
  }
  const hasScript = scriptState !== 'none';
  if (hasScript && (contextState === 'ready' || contextState === 'pending')) {
    return 'full';
  }
  const hasContext = contextState !== 'none';
  if (hasScript || hasContext) {
    return 'partial';
  }
  return 'empty';
};

// camelCase normalization is NOT applied to AITrigger_* identifiers, matching
// the lookup logic in assistant/providers/useRegionHandler.ts:74-83. Without
// this exception RAG items tagged with `AITrigger_Foo` would silently fail to
// attach to their script.
const normalizeForMatch = (value: string): string =>
  value.startsWith('AITrigger_') ? value : regionNameToCamelCase(value);

export const tagMatchesTrigger = (tag: string, trigger: string): boolean => {
  if (!tag || !trigger) {
    return false;
  }
  if (tag.toLowerCase() === trigger.toLowerCase()) {
    return true;
  }
  return normalizeForMatch(tag) === normalizeForMatch(trigger);
};

// Whether a tree entity is the current selection. Two ways to match:
//   1. Exact script id — clicking a configured row selects by its scriptId.
//   2. Region-name selection with no scriptId — e.g. "follow live trigger",
//      which selects by the live trigger name only. Match the entity's primary
//      trigger OR any alias, since a configured region script keeps the region
//      name as an alias while its primary is the SpotRegion_* spot. Without the
//      alias check, configured triggers would never match the live region name
//      (only unconfigured rows would), so the tree could not pan to them.
export const entityMatchesSelection = (
  entity: AiEntity,
  selectedScriptId: string | null,
  selectedTrigger: string | null,
): boolean => {
  if (entity.script?.scriptId && entity.script.scriptId === selectedScriptId) {
    return true;
  }
  if (selectedTrigger && !selectedScriptId) {
    if (tagMatchesTrigger(selectedTrigger, entity.primaryTrigger)) {
      return true;
    }
    return entity.aliases.some((alias) =>
      tagMatchesTrigger(selectedTrigger, alias),
    );
  }
  return false;
};

export interface AggregateParams {
  scripts: IAssistantScript[];
  ragItems: IAssistantRagVectorStorage[];
  mapName: string | undefined;
  brandingId: string | undefined;
  // Names of regions known to be AI triggers, sourced from the room map
  // metadata (`map.regions[].type === 'MytaAITrigger'`). Used to categorize
  // ambiguous trigger names like "Expo #1" that don't match the
  // `AITrigger_*` convention.
  knownAiTriggerNames?: string[];
}

// Map+branding scope filter. Applied to BOTH scripts and RAG items so the AI
// tree never leaks rows from other maps/brandings.
export const filterByScope = ({
  scripts,
  ragItems,
  mapName,
  brandingId,
}: AggregateParams): {
  scripts: IAssistantScript[];
  ragItems: IAssistantRagVectorStorage[];
} => ({
  scripts: scripts.filter((script) => {
    if (mapName && script.mapName !== mapName) {
      return false;
    }
    if (brandingId && script.brandingId !== brandingId) {
      return false;
    }
    return true;
  }),
  ragItems: ragItems.filter((item) => {
    if (brandingId && item.brandingId !== brandingId) {
      return false;
    }
    if (mapName && item.mapName && item.mapName !== mapName) {
      return false;
    }
    return true;
  }),
});

const buildOrphanKey = (
  tags: string[],
  brandingId: string | undefined,
): string => {
  const tag = tags.find(
    (t) => !brandingId || t.toLowerCase() !== brandingId.toLowerCase(),
  );
  return (tag ?? tags[0] ?? '__untagged__').toLowerCase();
};

export const aggregateEntities = (params: AggregateParams): AiEntity[] => {
  const { scripts: scopedScripts, ragItems: scopedRag } = filterByScope(params);
  const entities: AiEntity[] = [];
  const consumedRagIds = new Set<string>();
  const knownAiTriggerNames = new Set(params.knownAiTriggerNames ?? []);

  for (const script of scopedScripts) {
    const triggers = normalizeTriggers(script.scriptTrigger);
    const { primary, aliases } = splitPrimaryAndAliases(triggers);
    const matchingRag = scopedRag.filter((rag) =>
      rag.tags.some((tag) => triggers.some((t) => tagMatchesTrigger(tag, t))),
    );
    matchingRag.forEach((r) => consumedRagIds.add(r.ragVectorLinkId));

    const scriptState = deriveScriptState(script);
    const contextState = deriveContextState(matchingRag);
    const name =
      script.title?.trim() ||
      formatSpotRegion(primary) ||
      primary ||
      'Unnamed';

    entities.push({
      id: script.scriptId,
      kind: getScriptKind(script),
      name,
      primaryTrigger: primary,
      aliases,
      script,
      contextItems: matchingRag,
      scriptState,
      contextState,
      overallState: deriveOverallState(scriptState, contextState),
      category: deriveCategory(
        triggers,
        script.triggerType,
        knownAiTriggerNames,
      ),
    });
  }

  const orphans = scopedRag.filter(
    (rag) => !consumedRagIds.has(rag.ragVectorLinkId),
  );
  const knowledgeGroups = new Map<string, IAssistantRagVectorStorage[]>();
  for (const rag of orphans) {
    const key = buildOrphanKey(rag.tags, params.brandingId);
    const bucket = knowledgeGroups.get(key);
    if (bucket) {
      bucket.push(rag);
    } else {
      knowledgeGroups.set(key, [rag]);
    }
  }

  knowledgeGroups.forEach((items, key) => {
    const firstTag =
      items[0]?.tags.find((t) => t.toLowerCase() === key) ?? key;
    const contextState = deriveContextState(items);
    const name =
      items[0]?.title?.trim() || formatSpotRegion(firstTag) || firstTag;
    entities.push({
      id:
        'ctx:' +
        items
          .map((i) => i.ragVectorLinkId)
          .sort()
          .join(','),
      kind: 'ctx',
      name,
      primaryTrigger: firstTag,
      aliases: [],
      script: null,
      contextItems: items,
      scriptState: 'none',
      contextState,
      overallState: deriveOverallState('none', contextState),
      category: 'knowledge',
    });
  });

  return entities;
};

const matchesFilter = (
  filter: AiEntityFilter,
  entity: AiEntity,
): boolean => {
  if (filter === 'all') {
    return true;
  }
  if (filter === 'full') {
    return entity.overallState === 'full';
  }
  if (filter === 'partial') {
    return entity.overallState === 'partial';
  }
  return entity.overallState === 'error';
};

export const filterEntities = (
  entities: AiEntity[],
  filter: AiEntityFilter,
): AiEntity[] => entities.filter((entity) => matchesFilter(filter, entity));

export const countByFilter = (
  entities: AiEntity[],
): Record<AiEntityFilter, number> => ({
  all: entities.length,
  full: entities.filter((e) => e.overallState === 'full').length,
  partial: entities.filter((e) => e.overallState === 'partial').length,
  issues: entities.filter((e) => e.overallState === 'error').length,
});
